import { formatPrice } from './format';

// ── Types ────────────────────────────────────────────────────────────────────

export type ReadinessItemStatus = 'ok' | 'warn' | 'blocked' | 'neutral';

export interface ReadinessItem {
  /** Short label for the readiness dimension (e.g. «Карточка товара»). */
  label: string;
  /** Human-readable explanation of the current value/gate state. */
  detail: string;
  status: ReadinessItemStatus;
}

export type OverallReadiness = 'ready' | 'partial' | 'blocked';

export interface ReadinessResult {
  items: ReadinessItem[];
  overall: OverallReadiness;
  /** The first failing item that sets the overall verdict (null when ready). */
  primaryBlocker: ReadinessItem | null;
}

/**
 * Generic input shape — intentionally string-typed so both `ProductSummary`
 * and live form state can feed it without casting.
 */
export interface ReadinessInput {
  /**
   * `cardStatus` value. Defaults to 'ACTIVE' when absent (backend default).
   * Expected values: 'DRAFT' | 'ACTIVE' | 'ARCHIVED'
   */
  cardStatus?: string | null;
  /**
   * `commercialAvailability` value. Defaults to treating as AVAILABLE when absent.
   * Expected values: 'AVAILABLE' | 'PREORDER' | 'CHECK_AVAILABILITY' | 'COMING_SOON'
   *                  | 'OUT_OF_STOCK' | 'TEMPORARILY_UNAVAILABLE' | 'ARCHIVED'
   */
  commercialAvailability?: string | null;
  /** Whether the product is published to at least one sales channel. */
  published: boolean;
  /** Optional list of published channel codes for the detail label. */
  publishedChannels?: string[];
  /** Numeric price. `0` / `null` / `undefined` → price gate fails. */
  price?: number | null;
}

// ── Core computation ─────────────────────────────────────────────────────────

/**
 * Computes a four-gate readiness checklist for publication-and-sale readiness.
 *
 * The four gates mirror the backend's product-visibility chain:
 *  1. `cardStatus` — authoring gate (storefront eligibility)
 *  2. `commercialAvailability` — buyer-intent gate (primary CustomerState signal;
 *     when set it overrides stockStatus in ProductDocumentMapper)
 *  3. `published` — channel publication gate
 *  4. `price` — price governance gate (proxy: price > 0)
 *
 * Both `ProductDetailDrawer` and the hero section of `ProductEditPage` use this.
 * For the edit page, feed `draft.commercialAvailability` and `draft.price` so the
 * panel responds in real-time as the merchant changes the form.
 */
export function computeReadiness(input: ReadinessInput): ReadinessResult {
  const card = input.cardStatus ?? 'ACTIVE'; // backend default
  const comm = input.commercialAvailability;
  const { published, price } = input;

  const items: ReadinessItem[] = [];

  // ── 1. Authoring gate — cardStatus ────────────────────────────────────────
  if (card === 'ACTIVE') {
    items.push({
      label: 'Карточка товара',
      detail: 'Активна — допущена к публикации',
      status: 'ok',
    });
  } else if (card === 'DRAFT') {
    items.push({
      label: 'Карточка товара',
      detail: 'Черновик — активируйте перед публикацией',
      status: 'warn',
    });
  } else {
    // ARCHIVED
    items.push({
      label: 'Карточка товара',
      detail: 'В архиве — снята с витрины',
      status: 'blocked',
    });
  }

  // ── 2. Buyer-intent gate — commercialAvailability (PRIMARY buyer signal) ──
  if (!comm || comm === 'AVAILABLE') {
    items.push({
      label: 'Доступность',
      detail: 'Доступен к покупке',
      status: 'ok',
    });
  } else if (comm === 'PREORDER') {
    items.push({
      label: 'Доступность',
      detail: 'Предзаказ — принимаются заказы',
      status: 'ok',
    });
  } else if (comm === 'CHECK_AVAILABILITY') {
    items.push({
      label: 'Доступность',
      detail: 'Уточняйте наличие у менеджера',
      status: 'warn',
    });
  } else if (comm === 'COMING_SOON') {
    items.push({
      label: 'Доступность',
      detail: 'Скоро в продаже — заказ пока недоступен',
      status: 'warn',
    });
  } else if (comm === 'OUT_OF_STOCK') {
    items.push({
      label: 'Доступность',
      detail: 'Нет в наличии — заказ недоступен',
      status: 'warn',
    });
  } else if (comm === 'TEMPORARILY_UNAVAILABLE') {
    items.push({
      label: 'Доступность',
      detail: 'Временно недоступен',
      status: 'warn',
    });
  } else {
    // ARCHIVED
    items.push({
      label: 'Доступность',
      detail: 'Снят с продажи (архив) — недоступен покупателям',
      status: 'blocked',
    });
  }

  // ── 3. Channel gate — published ───────────────────────────────────────────
  const isHardBlocked = card === 'ARCHIVED' || comm === 'ARCHIVED';
  if (published) {
    const n = input.publishedChannels?.length ?? 0;
    const channelSuffix =
      n > 0 ? ` (${n} ${n === 1 ? 'канал' : n <= 4 ? 'канала' : 'каналов'})` : '';
    items.push({
      label: 'Публикация',
      detail: `Опубликован${channelSuffix}`,
      status: 'ok',
    });
  } else if (isHardBlocked) {
    // Don't penalise the score twice when archiving already sets the verdict.
    items.push({
      label: 'Публикация',
      detail: 'Не опубликован (архив)',
      status: 'neutral',
    });
  } else {
    items.push({
      label: 'Публикация',
      detail: 'Не опубликован — товар не виден на витрине',
      status: 'warn',
    });
  }

  // ── 4. Price gate — proxy: price > 0 ─────────────────────────────────────
  if (price != null && price > 0) {
    items.push({
      label: 'Цена',
      detail: formatPrice(price),
      status: 'ok',
    });
  } else {
    items.push({
      label: 'Цена',
      detail: 'Цена не указана — покупка невозможна',
      status: 'warn',
    });
  }

  // ── Overall verdict ───────────────────────────────────────────────────────
  const hasBlocked = items.some((i) => i.status === 'blocked');
  const hasWarn = items.some((i) => i.status === 'warn');
  const overall: OverallReadiness = hasBlocked ? 'blocked' : hasWarn ? 'partial' : 'ready';
  const primaryBlocker = items.find((i) => i.status === 'blocked' || i.status === 'warn') ?? null;

  return { items, overall, primaryBlocker };
}

// ── Verdict helpers (used by both Drawer and EditPage hero) ──────────────────

export function overallSeverity(overall: OverallReadiness): 'success' | 'warning' | 'error' {
  if (overall === 'ready') return 'success';
  if (overall === 'partial') return 'warning';
  return 'error';
}

export function overallMessage(overall: OverallReadiness): string {
  if (overall === 'ready') return 'Товар готов к продаже';
  if (overall === 'partial') return 'Требуются действия перед продажей';
  return 'Товар недоступен для покупателей';
}
